/**
 * @typedef {import('../types/Agent').default} Agent
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toAbsoluteUrl } from '../utils/toAbsoluteUrl';
import AgentHeader from '../components/AgentHeader/AgentHeader';
import Avm from '../components/Avm/Avm';
import Footer from '../components/Footer/Footer';
import PropertyDetails from '../components/PropertyDetails/PropertyDetails';
import Search from '../components/Search';
import StreetView from '../components/StreetView/StreetView';

const defaultPropertyImage = '/assets/images/default-property.svg';

/**
 * @typedef {{
 *  readonly city: string;
 *  readonly country: string;
 *  readonly postalCode: string;
 *  readonly state: string;
 *  readonly streetAddress1: string;
 *  readonly streetAddress2: string | null;
 * }} Address
 */

/**
 * @typedef {{
 *  readonly agent: Agent;
 * }} Props
 */

/**
 * @param {string | null} value
 * @returns {boolean}
 */
function isMissingAddressParam(value) {
	return !value || value === 'null';
}

/**
 * @param {URLSearchParams} searchParams
 * @returns {Address | null}
 */
function getAddressFromSearchParams(searchParams) {
	const city = searchParams.get('city');
	const country = searchParams.get('country');
	const postalCode = searchParams.get('postal_code');
	const state = searchParams.get('state');
	const streetAddress1 = searchParams.get('street_address_1');
	const streetAddress2Value = searchParams.get('street_address_2');
	const streetAddress2 =
		streetAddress2Value && streetAddress2Value !== 'null'
			? streetAddress2Value
			: null;

	const requiredValues = [streetAddress1, city, state, country, postalCode];

	if (requiredValues.some(isMissingAddressParam)) {
		return null;
	}

	return {
		city: /** @type {string} */ (city),
		country: /** @type {string} */ (country),
		postalCode: /** @type {string} */ (postalCode),
		state: /** @type {string} */ (state),
		streetAddress1: /** @type {string} */ (streetAddress1),
		streetAddress2,
	};
}

/**
 * @param {Props} props
 * @returns {JSX.Element}
 */
function ValuationPage(props) {
	const location = useLocation();
	const navigate = useNavigate();
	const searchParams = new URLSearchParams(location.search);
	const [address, setAddress] = useState(
		/** @type {Address | null} */ (null)
	);
	const [propertyImage, setPropertyImage] = useState(
		/** @type {string | null} */ (null)
	);
	const [valuationId, setValuationId] = useState(
		/** @type {string | null} */ (null)
	);

	useEffect(() => {
		setAddress(getAddressFromSearchParams(searchParams));
	}, [location.search]);

	/**
	 * @param {Address} values
	 * @returns {void}
	 */
	function handleOnSubmit(values) {
		const newSearchParams = new URLSearchParams(searchParams);
		newSearchParams.set('city', values.city);
		newSearchParams.set('country', values.country);
		newSearchParams.set('id', props.agent.id ?? '');
		newSearchParams.set('postal_code', values.postalCode);
		newSearchParams.set('state', values.state);
		newSearchParams.set('street_address_1', values.streetAddress1);
		newSearchParams.set(
			'street_address_2',
			values.streetAddress2 ?? 'null'
		);

		const newUrl = `${location.pathname}?${newSearchParams.toString()}`;

		setAddress(values);
		navigate(newUrl);
	}

	/**
	 * @param {string | null} id
	 * @returns {void}
	 */
	function handleValuationId(id) {
		setValuationId(id);
	}

	/**
	 * @param {string | null} imageUrl
	 * @returns {void}
	 */
	function handlePropertyImage(imageUrl) {
		setPropertyImage(imageUrl);
	}

	function formatAddress() {
		if (!address) {
			return '';
		}

		return `${address.streetAddress1}, ${address.city}, ${address.state}, ${address.postalCode}`;
	}

	if (!address) {
		return <Search onSubmit={handleOnSubmit} />;
	}

	return (
		<React.Fragment>
			<header className="h-ms-4 my-6 mx-2 w-full">
				{"Your Home's Estimated Value On The Market"}
			</header>
			<div className="app-container">
				<div className="card card-border p-4 my-6">
					<p className="my-4 -h-ms-4 text-grey-600">
						{formatAddress()}
					</p>
					<div className="card card-property-detail horizontal gap-6 justify-center">
						<figure>
							<img
								alt="A property"
								className="image rounded w-full h-full"
								src={toAbsoluteUrl(
									propertyImage ?? defaultPropertyImage
								)}
							/>
						</figure>
						<AgentHeader
							address={address}
							agent={props.agent}
							valuationId={valuationId}
						/>
					</div>
					<PropertyDetails
						address={address}
						handlePropertyImage={handlePropertyImage}
					/>
					<StreetView address={formatAddress()} />
					<Avm
						address={address}
						agentId={props.agent.id}
						appTitle={props.agent.appTitle}
						handleValuationId={handleValuationId}
					/>
					<Footer
						address={address}
						agent={props.agent}
						valuationId={valuationId}
					/>
				</div>
			</div>
		</React.Fragment>
	);
}

export default ValuationPage;
